import moment from 'moment';
import Api from '../Api';
import sendRequest from './sendRequest';
import checkAddOrganizationPayload from './checkAddOrganizationPayload';
import checkAddEventPayload from './checkAddEventPayload';
import {
  buildAllCitiesSelection,
  isAllCitiesSelection,
} from './allCitiesOfCountry';
import { parseHoldingDatesListForForm } from './formatHoldingDatesList';
import isValidDate from './isValidDate';

function cleanContacts(contacts = {}) {
  const next = { ...contacts };
  delete next.__v;
  delete next._id;
  return next;
}

function scheduleFromWorkTime(workTime = {}) {
  const next = { ...workTime };
  delete next.__v;
  delete next._id;

  return Object.keys(next).reduce((acc, key) => {
    const thisDayType = next[key];
    if (typeof thisDayType === 'string' && thisDayType.length === 11 && thisDayType.includes('-')) {
      const [start, end] = thisDayType.split('-');
      acc[key] = { start, end, type: 'hours' };
    } else if (thisDayType && typeof thisDayType === 'object') {
      acc[key] = thisDayType;
    } else {
      acc[key] = { start: '', end: '', type: thisDayType || 'closed' };
    }
    return acc;
  }, {});
}

function convertScheduleForPayload(schedule) {
  return Object.keys(schedule || {}).reduce((acc, day) => ({
    ...acc,
    [day]: schedule[day].type === 'hours'
      ? `${schedule[day].start}-${schedule[day].end}`
      : schedule[day].type,
  }), {});
}

function resolveCategory(item, categories = []) {
  const id = item?.category?._id || item?.category;
  if (!id) return null;
  return categories.find((category) => category._id === id) || item.category || null;
}

function resolveSubCategories(item, category) {
  const selected = item?.sub_categories || [];
  if (!category?.sub_categories?.length) return [];
  return selected
    .map((sub) => category.sub_categories.find((itemSub) => itemSub._id === (sub._id || sub)))
    .filter(Boolean);
}

function resolveLanguages(item, languagesCatalog = []) {
  const selected = item?.languages || [];
  if (!selected.length) return [];
  if (!languagesCatalog.length) return selected;
  return selected
    .map((lang) => languagesCatalog.find((itemLang) => itemLang._id === (lang._id || lang)))
    .filter(Boolean);
}

function resolveCity(item) {
  if (item?.is_in_all_cities_of_country) {
    return buildAllCitiesSelection(item.country);
  }
  if (!item?.city) return null;
  return { ...item.city, is_in_all_cities_of_country: false };
}

export async function ensureFullOrganization(item) {
  if (item?.work_time && item?.contacts && item?.user?._id && item?.name) {
    return item;
  }
  const data = await sendRequest({
    request: Api.getOrganization,
    payload: { id: item._id },
    warnErrorText: 'while loading organization for bulk edit',
  });
  if (data?.status === 'ok' && data.organization) return data.organization;
  return item;
}

export async function ensureFullEvent(item) {
  if (item?.contacts && item?.user?._id && item?.name) {
    return item;
  }
  const data = await sendRequest({
    request: Api.getEvent,
    payload: { id: item._id },
    warnErrorText: 'while loading event for bulk edit',
  });
  if (data?.status === 'ok' && data.event) return data.event;
  return item;
}

/**
 * overrides: { category, subCategories, city, languages, isForAllLanguages }
 * Enabled fields only — undefined means keep existing.
 */
export function buildOrganizationEditPayload(item, overrides = {}, catalogs = {}) {
  const { categories = [], languages: languagesCatalog = [] } = catalogs;

  const category = overrides.category !== undefined
    ? overrides.category
    : resolveCategory(item, categories);

  let subCategories;
  if (overrides.subCategories !== undefined) {
    subCategories = overrides.subCategories || [];
  } else if (overrides.category !== undefined) {
    const allowed = new Set((category?.sub_categories || []).map((sub) => sub._id));
    subCategories = resolveSubCategories(item, category).filter((sub) => allowed.has(sub._id));
  } else {
    // Не трогаем подкатегории записи, даже если в справочнике категория без nested sub_categories.
    subCategories = (item.sub_categories || []).map((sub) => (
      sub?._id ? sub : { _id: sub }
    ));
  }

  const city = overrides.city !== undefined ? overrides.city : resolveCity(item);
  const isInAllCities = isAllCitiesSelection(city);
  const languages = overrides.languages !== undefined
    ? (overrides.languages || [])
    : resolveLanguages(item, languagesCatalog);
  const isForAllLanguages = overrides.isForAllLanguages !== undefined
    ? Boolean(overrides.isForAllLanguages)
    : (item.is_for_all_languages === undefined ? true : Boolean(item.is_for_all_languages));

  const schedule = scheduleFromWorkTime(item.work_time);
  const contacts = cleanContacts(item.contacts);

  return checkAddOrganizationPayload({
    owner_id: item.user?._id || item.owner_id || '',
    name: item.name || '',
    category_id: category?._id || '',
    sub_category_ids: (subCategories || []).map((sub) => sub._id),
    must_set_subcategories: overrides.category !== undefined || overrides.subCategories !== undefined
      ? Boolean(category?.sub_categories?.length)
      : false,
    country_id: city?.country_id || item.country?._id || '',
    city_id: isInAllCities ? undefined : city?._id,
    additionalCities: isInAllCities ? [] : (item.additional_cities || []),
    specialization: item.specialization || '',
    address: isInAllCities ? '' : (item.address || ''),
    coordinates: isInAllCities ? null : (item.coordinates || null),
    workTime: convertScheduleForPayload(schedule),
    contacts,
    description: item.description || '',
    languages,
    is_for_all_languages: isForAllLanguages,
    tags: item.tags || [],
    photos: [],
    templatePhotoIds: (item.template_photos || []).map((photo) => photo._id || photo).filter(Boolean),
    withPhotos: false,
    isOnlineWorking: Boolean(category?.is_online),
    is_in_all_cities_of_country: isInAllCities,
  });
}

export function buildEventEditPayload(item, overrides = {}, catalogs = {}) {
  const { languages: languagesCatalog = [] } = catalogs;

  const eventCategory = overrides.category !== undefined
    ? overrides.category
    : (item.events_category || null);

  const city = overrides.city !== undefined ? overrides.city : resolveCity(item);
  const isInAllCities = isAllCitiesSelection(city);
  const languages = overrides.languages !== undefined
    ? (overrides.languages || [])
    : resolveLanguages(item, languagesCatalog);
  const isForAllLanguages = overrides.isForAllLanguages !== undefined
    ? Boolean(overrides.isForAllLanguages)
    : (item.is_for_all_languages === undefined ? true : Boolean(item.is_for_all_languages));

  const parsedDates = parseHoldingDatesListForForm(item.holding_dates_list);
  const hasStructuredDates = Array.isArray(item.holding_dates_list) && item.holding_dates_list.length > 0;
  const contacts = cleanContacts(item.contacts);

  return checkAddEventPayload({
    owner_id: item.user?._id || item.owner_id || '',
    name: item.name || '',
    country_id: city?.country_id || item.country?._id || '',
    city_id: isInAllCities ? undefined : city?._id,
    address: isInAllCities ? '' : (item.address || ''),
    coordinates: isInAllCities ? null : (item.coordinates || null),
    holding_dates: parsedDates.dates,
    holding_time: parsedDates.time,
    date_start: isValidDate(new Date(item.date_start)) ? moment(item.date_start).format('YYYY-MM-DD') : '',
    date_end: isValidDate(new Date(item.date_end)) ? moment(item.date_end).format('YYYY-MM-DD') : '',
    contacts,
    description: item.description || '',
    photos: [],
    withPhotos: false,
    events_category_id: eventCategory?._id || '',
    is_in_all_cities_of_country: isInAllCities,
    allow_missing_holding_dates: Boolean(!hasStructuredDates && item.holding_date),
    languages,
    is_for_all_languages: isForAllLanguages,
  });
}

export async function saveBulkEditedItems({
  entityType,
  items,
  overrides,
  catalogs,
  onProgress,
}) {
  const total = items.length;
  let successCount = 0;
  const failures = [];

  for (let i = 0; i < items.length; i += 1) {
    const rawItem = items[i];
    onProgress?.({ current: i + 1, total, name: rawItem.name || rawItem._id });

    try {
      const fullItem = entityType === 'organization'
        ? await ensureFullOrganization(rawItem)
        : await ensureFullEvent(rawItem);

      const built = entityType === 'organization'
        ? buildOrganizationEditPayload(fullItem, overrides, catalogs)
        : buildEventEditPayload(fullItem, overrides, catalogs);

      if (built.errors?.length) {
        failures.push({
          id: rawItem._id,
          name: rawItem.name || 'без названия',
          message: built.errors.join('; '),
        });
        continue;
      }

      const data = await sendRequest({
        request: entityType === 'organization' ? Api.editOrganization : Api.editEvent,
        payload: { id: rawItem._id, payload: built.payload },
        warnErrorText: `while bulk editing ${entityType}`,
      });

      if (data?.status === 'ok') {
        successCount += 1;
      } else {
        failures.push({
          id: rawItem._id,
          name: rawItem.name || 'без названия',
          message: typeof data?.message === 'string' ? data.message : 'Не удалось сохранить',
        });
      }
    } catch (error) {
      failures.push({
        id: rawItem._id,
        name: rawItem.name || 'без названия',
        message: error?.message || 'Неизвестная ошибка',
      });
    }
  }

  return { total, successCount, failures };
}
